import type { FC, FormEvent, ReactNode } from 'react'
import type { FieldErrors } from '@/types'
import type { BookingField, BookingValues } from '@/lib/forms'

import { useEffect, useState } from 'react'
import { CalendarCheck } from 'lucide-react'
import { EMPTY_BOOKING, validateBooking } from '@/lib/forms'
import { formatLongDate, formatPrice, toDateString } from '@/lib/format'
import { accentButton, ghostButton } from '@/components/generals/button-styles'
import {
    FormField,
    Honeypot,
    describedBy,
    inputClass
} from '@/components/generals/form-field'
import { WORKSHOP } from '@/constants/content'

type Status = 'idle' | 'sending' | 'error' | 'done'

interface Confirmation {
    reference: string
    total: number
    date: string
    guests: number
    email: string
}

const DAY = 24 * 60 * 60 * 1000

const BookingForm: FC = (): ReactNode => {
    const [values, setValues] = useState<BookingValues>(EMPTY_BOOKING)
    const [errors, setErrors] = useState<FieldErrors<BookingField>>({})
    const [status, setStatus] = useState<Status>('idle')
    const [message, setMessage] = useState('')
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
    const [range, setRange] = useState({ min: '', max: '' })

    // Date limits depend on the visitor's own calendar, so they are set after the page loads
    useEffect(() => {
        const now = Date.now()
        setRange({ min: toDateString(new Date(now + DAY)), max: toDateString(new Date(now + 365 * DAY)) })
    }, [])

    const update = <K extends keyof BookingValues>(field: K, value: BookingValues[K]): void => {
        setValues(current => ({ ...current, [field]: value }))
        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const total = Math.max(0, values.guests) * WORKSHOP.pricePerPerson

    const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        const { errors: found } = validateBooking(values, range.min, range.max)
        if (Object.keys(found).length > 0) {
            setErrors(found)
            setStatus('error')
            setMessage('Please check the highlighted fields.')
            return
        }

        setStatus('sending')
        setMessage('')

        try {
            const response = await fetch('/api/bookings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(values)
            })
            const result = await response.json().catch(() => ({}))

            if (!response.ok || !result.ok) {
                setErrors(result.fields ?? {})
                setStatus('error')
                setMessage(result.error ?? "We couldn't save your booking just now. Please try again in a moment.")
                return
            }

            setConfirmation({ reference: result.reference, total: result.total, date: values.date, guests: values.guests, email: values.email })
            setStatus('done')
        } catch {
            setStatus('error')
            setMessage('Your connection dropped before the booking reached us. Please try again.')
        }
    }

    const reset = (): void => {
        setValues(EMPTY_BOOKING)
        setErrors({})
        setConfirmation(null)
        setStatus('idle')
    }

    if (status === 'done' && confirmation) {
        return (
            <div role='status' className='animate-in fade-in slide-in-from-bottom-2 duration-700'>
                <span className='inline-flex size-12 items-center justify-center rounded-full bg-bark text-paper'>
                    <CalendarCheck className='size-5' strokeWidth={1.75} />
                </span>
                <h3 className='mt-6 font-heading text-[2rem] font-light leading-tight text-bark'>
                    Your morning is pencilled in.
                </h3>
                <p className='mt-4 text-[1.02rem] leading-relaxed text-bark-soft'>
                    {formatLongDate(confirmation.date)}, {confirmation.guests} {confirmation.guests === 1 ? 'guest' : 'guests'}, {formatPrice(confirmation.total)} paid on the day. Your reference is <strong className='font-semibold text-bark'>{confirmation.reference}</strong>.
                </p>
                <p className='mt-3 text-[1.02rem] leading-relaxed text-bark-soft'>
                    We will confirm by email to <strong className='font-semibold text-bark'>{confirmation.email}</strong> within a day, with directions for your driver.
                </p>
                <button type='button' onClick={reset} className={`${ghostButton} mt-8`}>
                    Book another date
                </button>
            </div>
        )
    }

    return (
        <form noValidate onSubmit={submit} className='relative' aria-describedby={message ? 'booking-status' : undefined}>
            <Honeypot value={values.company} onChange={value => update('company', value)} />

            <div className='grid gap-5 sm:grid-cols-2'>
                <FormField id='booking-date' label='Date' error={errors.date} hint={`${WORKSHOP.days}, starting at ${WORKSHOP.start}`}>
                    <input
                        id='booking-date'
                        type='date'
                        min={range.min || undefined}
                        max={range.max || undefined}
                        value={values.date}
                        onChange={event => update('date', event.target.value)}
                        aria-invalid={Boolean(errors.date)}
                        aria-describedby={describedBy('booking-date', errors.date, 'hint')}
                        className={`${inputClass} field-date`}
                    />
                </FormField>

                <FormField id='booking-guests' label='Number of people' error={errors.guests}>
                    <select
                        id='booking-guests'
                        value={values.guests}
                        onChange={event => update('guests', Number(event.target.value))}
                        aria-invalid={Boolean(errors.guests)}
                        aria-describedby={describedBy('booking-guests', errors.guests)}
                        className={inputClass}
                    >
                        {Array.from({ length: WORKSHOP.maxGuests }, (_, index) => index + 1).map(count => (
                            <option key={count} value={count}>
                                {count} {count === 1 ? 'person' : 'people'}
                            </option>
                        ))}
                    </select>
                </FormField>

                <FormField id='booking-name' label='Your name' error={errors.fullName}>
                    <input
                        id='booking-name'
                        type='text'
                        autoComplete='name'
                        value={values.fullName}
                        onChange={event => update('fullName', event.target.value)}
                        aria-invalid={Boolean(errors.fullName)}
                        aria-describedby={describedBy('booking-name', errors.fullName)}
                        className={inputClass}
                    />
                </FormField>

                <FormField id='booking-email' label='Email' error={errors.email}>
                    <input
                        id='booking-email'
                        type='email'
                        autoComplete='email'
                        inputMode='email'
                        value={values.email}
                        onChange={event => update('email', event.target.value)}
                        aria-invalid={Boolean(errors.email)}
                        aria-describedby={describedBy('booking-email', errors.email)}
                        className={inputClass}
                    />
                </FormField>
            </div>

            <div className='mt-8 flex flex-col gap-5 border-t border-bark/12 pt-6 sm:flex-row sm:items-center sm:justify-between'>
                <p className='text-bark-soft'>
                    <span className='font-heading text-[1.7rem] font-light text-pomegranate'>{formatPrice(total)}</span>
                    <span className='ml-2 text-sm'>paid on the day, cash or card</span>
                </p>
                <button type='submit' disabled={status === 'sending'} className={accentButton}>
                    <CalendarCheck className='size-4' strokeWidth={1.75} />
                    {status === 'sending' ? 'Saving your place' : 'Request this date'}
                </button>
            </div>

            <p id='booking-status' role='status' className='mt-4 min-h-5 text-sm text-pomegranate'>
                {status === 'error' ? message : ''}
            </p>
        </form>
    )
}

export default BookingForm
